import { useEffect, useMemo, useState } from 'react';
import {
  PrismBadge,
  PrismButton,
  PrismSelect,
  PrismSwitch,
} from '@prism-ui/react';
import { DateTime } from 'luxon';

export interface RetentionSchedule {
  scheduleEnabled: boolean;
  scheduleHour: number;
  scheduleMinute: number;
  scheduleDayOfMonth: number;
  lastRunAt: string | null;
  lastRunStatus: string | null;
  lastRunTrigger: string | null;
  lastRunError: string | null;
  nextRunAt: string | null;
  timezone: string;
}

interface RetentionScheduleControlsProps {
  subject: string;
  schedule: RetentionSchedule;
  disabled: boolean;
  onSave: (patch: {
    scheduleEnabled: boolean;
    scheduleHour: number;
    scheduleMinute: number;
    scheduleDayOfMonth: number;
  }) => Promise<void>;
}

// The runner ticks every 15 minutes, so offering finer granularity than that would show a
// precision the scheduler cannot honour.
const MINUTE_OPTIONS = [0, 15, 30, 45].map((minute) => ({
  value: String(minute),
  display: String(minute).padStart(2, '0'),
}));

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, hour) => ({
  value: String(hour),
  display: `${String(hour).padStart(2, '0')}:00`,
}));

// Capped at 28 to match the column constraint: a schedule set to the 30th would silently
// skip February, which is worse than a day every month actually has.
const DAY_OPTIONS = Array.from({ length: 28 }, (_, index) => ({
  value: String(index + 1),
  display: String(index + 1),
}));

// Schedule times are shown in the zone the scheduler actually runs in (UTC), with the
// viewer's own time alongside for readability. Showing only a local rendering would make two
// admins in different regions read different times for the same run.
const formatTimestamp = (value: string | null, zone: string) => {
  if (!value) return null;
  const parsed = DateTime.fromISO(value).setZone(zone);
  if (!parsed.isValid) return null;

  const scheduled = parsed.toFormat('LLL d, yyyy • HH:mm ZZZZ');
  const local = DateTime.fromISO(value).toLocal();

  // Suppress the duplicate when the viewer is already in the scheduler's zone.
  if (local.offset === parsed.offset) return scheduled;

  return `${scheduled} — ${local.toFormat('LLL d, yyyy • HH:mm ZZZZ')} your time`;
};

const ordinal = (day: number) => {
  const remainderTen = day % 10;
  const remainderHundred = day % 100;
  if (remainderTen === 1 && remainderHundred !== 11) return `${day}st`;
  if (remainderTen === 2 && remainderHundred !== 12) return `${day}nd`;
  if (remainderTen === 3 && remainderHundred !== 13) return `${day}rd`;
  return `${day}th`;
};

/**
 * Schedule and health for a single retention policy.
 *
 * The switch is part of the draft rather than saving on flip. Enabling retention is the one
 * action here that can destroy data unattended, so it takes a deliberate second step, and an
 * accidental tap can be abandoned with Cancel.
 */
export function RetentionScheduleControls({
  subject,
  schedule,
  disabled,
  onSave,
}: RetentionScheduleControlsProps) {
  const [enabled, setEnabled] = useState(schedule.scheduleEnabled);
  const [hour, setHour] = useState(schedule.scheduleHour);
  const [minute, setMinute] = useState(schedule.scheduleMinute);
  const [dayOfMonth, setDayOfMonth] = useState(schedule.scheduleDayOfMonth);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setEnabled(schedule.scheduleEnabled);
    setHour(schedule.scheduleHour);
    setMinute(schedule.scheduleMinute);
    setDayOfMonth(schedule.scheduleDayOfMonth);
  }, [
    schedule.scheduleEnabled,
    schedule.scheduleHour,
    schedule.scheduleMinute,
    schedule.scheduleDayOfMonth,
  ]);

  const dirty = enabled !== schedule.scheduleEnabled
    || hour !== schedule.scheduleHour
    || minute !== schedule.scheduleMinute
    || dayOfMonth !== schedule.scheduleDayOfMonth;

  const status = useMemo(() => {
    if (schedule.lastRunStatus === 'success') {
      return { tone: 'success' as const, label: 'Healthy' };
    }
    if (schedule.lastRunStatus === 'failure') {
      return { tone: 'danger' as const, label: 'Last run failed' };
    }
    return { tone: 'caution' as const, label: 'Never run' };
  }, [schedule.lastRunStatus]);

  const lastRun = formatTimestamp(schedule.lastRunAt, schedule.timezone);
  const nextRun = formatTimestamp(schedule.nextRunAt, schedule.timezone);
  const scheduleLabel = `${ordinal(dayOfMonth)} of each month at ${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;

  const reset = () => {
    setEnabled(schedule.scheduleEnabled);
    setHour(schedule.scheduleHour);
    setMinute(schedule.scheduleMinute);
    setDayOfMonth(schedule.scheduleDayOfMonth);
  };

  const save = async () => {
    setSaving(true);
    try {
      await onSave({
        scheduleEnabled: enabled,
        scheduleHour: hour,
        scheduleMinute: minute,
        scheduleDayOfMonth: dayOfMonth,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="clr:flex clr:flex-col clr:gap-sm clr:border-t clr:border-primary-base clr:pt-md">
      <div className="clr:flex clr:flex-wrap clr:items-center clr:justify-between clr:gap-sm">
        <strong>Scheduled {subject.toLowerCase()} retention</strong>
        <div className="clr:flex clr:items-center clr:gap-sm">
          <PrismBadge tone={status.tone}>{status.label}</PrismBadge>
          <PrismBadge tone={schedule.scheduleEnabled ? 'success' : 'caution'}>
            {schedule.scheduleEnabled ? 'ON' : 'OFF'}
          </PrismBadge>
        </div>
      </div>

      <PrismSwitch
        label={`Run ${subject.toLowerCase()} retention automatically`}
        checked={enabled}
        disabled={disabled || saving}
        onPrismChange={(event: CustomEvent<{ checked?: boolean; value?: boolean }>) => {
          setEnabled(Boolean(event.detail?.checked ?? event.detail?.value));
        }}
      />

      <div className="clr:flex clr:flex-wrap clr:items-end clr:gap-sm">
        <PrismSelect
          label="Day of month"
          value={String(dayOfMonth)}
          options={DAY_OPTIONS}
          disabled={disabled || saving}
          onPrismChange={(event: CustomEvent<{ value?: string }>) => {
            setDayOfMonth(Number(event.detail?.value || schedule.scheduleDayOfMonth));
          }}
        />
        <PrismSelect
          label="Hour"
          value={String(hour)}
          options={HOUR_OPTIONS}
          disabled={disabled || saving}
          onPrismChange={(event: CustomEvent<{ value?: string }>) => {
            setHour(Number(event.detail?.value ?? schedule.scheduleHour));
          }}
        />
        <PrismSelect
          label="Minute"
          value={String(minute)}
          options={MINUTE_OPTIONS}
          disabled={disabled || saving}
          onPrismChange={(event: CustomEvent<{ value?: string }>) => {
            setMinute(Number(event.detail?.value ?? schedule.scheduleMinute));
          }}
        />
      </div>

      <p className="clr:text-sm clr:text-secondary-base">
        {enabled
          ? `Runs on the ${scheduleLabel}, ${schedule.timezone}.`
          : `Nothing runs automatically. When enabled, this would run on the ${scheduleLabel}, ${schedule.timezone}.`}
        {' '}Schedules and audit timestamps are kept in {schedule.timezone} so every chapter
        reads the same run history regardless of where they are.
      </p>

      <dl className="clr:grid clr:grid-cols-2 clr:gap-xs clr:text-sm">
        <dt>Last run</dt>
        <dd>
          {lastRun
            ? `${lastRun}${schedule.lastRunTrigger ? ` (${schedule.lastRunTrigger})` : ''}`
            : 'Never'}
        </dd>
        <dt>Next scheduled run</dt>
        <dd>{nextRun || 'Not scheduled'}</dd>
      </dl>

      {schedule.lastRunStatus === 'failure' && schedule.lastRunError && (
        <p className="clr:text-sm clr:text-error-base">
          Last error: {schedule.lastRunError}
        </p>
      )}

      {dirty && (
        <div className="clr:flex clr:flex-wrap clr:justify-center clr:gap-sm">
          <PrismButton
            tone="secondary"
            display="outline"
            disabled={saving}
            onPrismClick={reset}
          >
            Cancel schedule changes
          </PrismButton>
          <PrismButton
            tone="success"
            loading={saving}
            disabled={saving}
            onPrismClick={save}
          >
            Save schedule
          </PrismButton>
        </div>
      )}
    </div>
  );
}
